import { FC, useEffect, useState } from 'react';
import { VisitorPass, VisitorPasses } from '../../models/IVisitorPass';
import { getPassesByPageAndFilter } from '../../services/pass-service';
import { useUserContext } from '../../context/user';
import { useI18nContext } from '../../context/language/use-i18n-context';
import ts from './i18n';
import { IconButton, Table, TableCell, TableHeading, Skeleton } from '@nike/eds';
import { formatDate } from '../../utils/date-helper';
import dayjs from 'dayjs';
import { AppConfig } from '../../utils/constants';
import styles from './visitor-pass.module.css';
import { PASSSTATUSES, PASSTYPES } from './options';
import { VisitorPassHeader } from './visitorpass-header/visitorpass-header';
import {
  EditPassProps,
  FilterStatusAndPage,
  Option,
  Props,
  VisitorPassCriteria,
  VistorPassProps,
} from './type';
import { CancelPassModal } from './cancel-pass/cancel-pass';
import { ResendEmailModal } from './resend-email/resend-email';
import Pass from '../../components/passes/pass_container/pass';
import LegalTextBanner from '../../components/shared/legaltext-banner/legaltext-banner';
import { useLanguageContext } from 'context/language/context';
import { Languages } from 'models/translations';
type TsKeys = keyof typeof ts;
export const VisitorPassGrid: FC<Props> = ({ passPolicies, isModelOpen, setSnackBar }) => {
  const { i18nString } = useI18nContext();
  const [visitorPasses, setVisitorPasses] = useState<VisitorPasses>();
  const [sorting, setSorting] = useState<{ key: string; asc: boolean }>({
    key: 'name',
    asc: true,
  });
  const [visitorPassFilter, setVisitorPassFilter] = useState<VisitorPassCriteria>({
    passType: 0,
    passStatus: 0,
    pageSize: 10,
    currentPage: 1,
  });
  const [passTypes, setPassTypes] = useState<Option[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const userInfo = useUserContext();
  const [language] = useLanguageContext();
  const [passProps, setPassProps] = useState<VistorPassProps>({
    pass: {} as VisitorPass,
    onDismiss: () => setPassProps({ ...passProps, isVisible: false, isResendVisible: false }),
    isVisible: false,
    isResendVisible: false,
  });
  const getName = (pass: VisitorPass) => {
    if (language === Languages.JA) {
      return pass.lastName + ' ' + pass.firstName;
    }
    return pass.firstName + ' ' + pass.lastName;
  };
  const columns = [
    {
      headerName: ts.GRID_HEADER_NAME.stringKey,
      field: language === Languages.JA ? 'lastName' : 'firstName',
    },
    {
      headerName: ts.GRID_HEADER_COMPANY.stringKey,
      field: 'companyName',
    },
    {
      headerName: ts.GRID_HEADER_TYPE.stringKey,
      field: 'passTypeToken',
    },
    {
      headerName: ts.GRID_HEADER_PASS_TYPE.stringKey,
      field: 'passPolicy',
    },
    { headerName: ts.GRID_HEADER_DATE.stringKey, field: 'date' },
    { headerName: 'PASS NUMBER', field: 'alternateId' },
    {
      headerName: ts.GRID_HEADER_STATUS.stringKey,
      field: 'statusToken',
    },
  ];

  useEffect(() => {
    const psTypes: Option[] = [PASSTYPES[0]];
    const availablePasstypeIds = new Set(passPolicies?.map(pol => pol.passTypeId));

    PASSTYPES.forEach(passType => {
      if (availablePasstypeIds.has(passType.value)) {
        psTypes.push(passType);
      }
    });
    setPassTypes(psTypes);
  }, [passPolicies]);

  const filterAndPageChange = (value: FilterStatusAndPage) => {
    setVisitorPassFilter({
      passType: value.passType?.value || 0,
      passStatus: value.passStatus?.value || 0,
      pageSize: 10,
      currentPage: value.page,
    });
  };
  const [editPassProps, setEditPassProps] = useState<EditPassProps>({
    modalOpen: false,
    onDismiss: () => setEditPassProps({ ...editPassProps, modalOpen: false }),
    passToEdit: null,
  });
  useEffect(() => {
    const sponsorId = userInfo?.passplayUser?.sponsorId;
    if (
      sponsorId &&
      isModelOpen === false &&
      passProps.isVisible === false &&
      editPassProps.modalOpen === false
    ) {
      setLoading(true);
      const visitStatus = visitorPassFilter.passStatus === 0 ? '0,1,4,8' : '';
      const visitType = visitorPassFilter.passType === 0 ? null : visitorPassFilter.passType;
      getPassesByPageAndFilter(
        sponsorId,
        visitorPassFilter.currentPage,
        visitorPassFilter.pageSize,
        visitType,
        visitStatus,
      )
        .then(passes => {
          passes.data.passes.forEach(
            pass => (pass.date = formatDate(pass, userInfo?.passplayUser?.userCountry || '')),
          );
          setVisitorPasses({ passes: passes.data.passes, metadata: passes.data.metadata });
          setSorting({ key: 'firstName', asc: true });
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [visitorPassFilter, passPolicies, isModelOpen, passProps.isVisible, editPassProps.modalOpen]);

  const randomKey = () => {
    return Math.random().toString(36).substring(2, 9);
  };
  const applySorting = (key: string) => {
    if (sorting.key === key) {
      setSorting({ ...sorting, asc: !sorting.asc });
    } else {
      setSorting({ key: key, asc: true });
    }
  };

  useEffect(() => {
    if (!visitorPasses?.passes || visitorPasses.passes.length === 0) return;
    const sortedPasses = (visitorPasses?.passes ?? []).sort((a, b) => {
      if (sorting.key === 'date') {
        // Handle date as "MM/DD/YY - MM/DD/YY" or single date
        const parseDate = (val: string | undefined) => {
          if (!val) return null;
          // Take the first date in the range or the single date
          const firstDate = val.split('-')[0].trim();
          return dayjs(firstDate, ['MM/DD/YY', 'MM/DD/YYYY']);
        };
        const aDate = parseDate(a[sorting.key] as string | undefined);
        const bDate = parseDate(b[sorting.key] as string | undefined);

        if ((!aDate || !aDate.isValid()) && (!bDate || !bDate.isValid())) return 0;
        if (!aDate || !aDate.isValid()) return sorting.asc ? 1 : -1;
        if (!bDate || !bDate.isValid()) return sorting.asc ? -1 : 1;

        return sorting.asc ? aDate.diff(bDate) : bDate.diff(aDate);
      }
      const valueA = a[sorting.key]?.toString().toLowerCase() || '';
      const valueB = b[sorting.key]?.toString().toLowerCase() || '';

      // Default string/number sorting
      if (valueA < valueB) {
        return sorting.asc ? -1 : 1;
      }
      if (valueA > valueB) {
        return sorting.asc ? 1 : -1;
      }
      return 0;
    });

    if (visitorPasses?.metadata) {
      setVisitorPasses({ passes: sortedPasses, metadata: visitorPasses.metadata });
    }
  }, [sorting]);

  const disableEdit = (pass: VisitorPass) => {
    const policy = passPolicies?.find(pol => pol.passTypeId === pass.passTypeId);
    const passesRemaining =
      (policy?.passesAllowed || 0) -
      (policy?.passesUsed == null
        ? 0
        : policy?.passesUsed > policy?.passesAllowed
          ? policy?.passesAllowed
          : policy?.passesUsed);
    if (pass.passTypeId === AppConfig.passType.vip) {
      if (policy?.operationalFlags.canEdit) {
        if (
          (pass.passStatusId === AppConfig.passStatus.used ||
            pass.passStatusId === AppConfig.passStatus.checkedIn ||
            pass.passStatusId === AppConfig.passStatus.expired) &&
          passesRemaining <= 0
        ) {
          return true;
        }
      } else return true;
    }
    if (
      pass.passStatusId === AppConfig.passStatus.used ||
      pass.passStatusId === AppConfig.passStatus.checkedIn ||
      pass.passStatusId === AppConfig.passStatus.expired
    ) {
      return true;
    }
    return false;
  };

  const disableDecline = (pass: VisitorPass) => {
    const policy = passPolicies?.find(pol => pol.passTypeId === pass.passTypeId);
    if (
      pass.passStatusId == AppConfig.passStatus.canceled ||
      pass.passStatusId === AppConfig.passStatus.expired ||
      pass.passStatusId === AppConfig.passStatus.used ||
      pass.passStatusId === AppConfig.passStatus.checkedIn
    ) {
      return true;
    }
    if (policy?.passTypeId === AppConfig.passType.vip) {
      if (policy?.operationalFlags.canCancel) {
        return false;
      }
      return true;
    }
    if (policy?.policyLocationId === 156) {
      return true;
    }
    return false;
  };

  // const isMexicoAndHasPromocode = () => {
  //   const isMexico = userInfo?.passplayUser?.userCountry?.toLowerCase() === 'mexico';
  //   const hasPromocode = visitorPasses?.passes?.some(
  //     pass => pass.alternateId.indexOf(pass.passId.toString()) == -1,
  //   );
  //   return isMexico && hasPromocode;
  // };

  const showPromocodeColumn = () => {
    const userCountry = userInfo?.passplayUser?.userCountry?.toLowerCase();
    const restrictedCountries = ['china', 'hong kong', 'taiwan', 'macau'];
    const verifyCountry = !restrictedCountries.includes(userCountry || '');
    const hasPromocode = visitorPasses?.passes?.some(
      pass => pass.alternateId.indexOf(pass.passId.toString()) == -1,
    );
    return verifyCountry && hasPromocode;
  };

  const getTranslation = (key: string) => {
    if (!ts[key as TsKeys]) {
      return key; // Fallback to the key itself if translation is missing
    }
    return i18nString(ts[key as TsKeys]);
  };

  const disableResendEmail = (pass: VisitorPass) => {
    const policy = passPolicies?.find(pol => pol.passTypeId === pass.passTypeId);
    if (
      !pass.emailAddress ||
      pass.passStatusId === AppConfig.passStatus.canceled ||
      pass.passStatusId === AppConfig.passStatus.expired ||
      pass.passStatusId === AppConfig.passStatus.checkedIn ||
      (pass.passLocationId !== 32000 && pass.passStatusId == AppConfig.passStatus.used)
    )
      return true;
    if (policy?.passTypeId === AppConfig.passType.vip) {
      if (policy?.operationalFlags.canCancel) {
        return false;
      }
      return true;
    }
    if (policy?.policyLocationId === 156) {
      return true;
    }
    return false;
  };

  return (
    <section className="eds-spacing--px-48">
      <VisitorPassHeader
        passTypes={passTypes}
        pageData={{
          currentPage: visitorPasses?.metadata.currentPage || 0,
          totalPages: visitorPasses?.metadata.totalPages || 0,
        }}
        passStatusOpts={PASSSTATUSES}
        handleFilterAndPageChange={filterAndPageChange}
      ></VisitorPassHeader>
      <LegalTextBanner />
      {loading ? (
        <>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton
              data-testid={`pass-skeleton ${index}`}
              key={index}
              height={50}
              width={`${100}%`}
              style={{ margin: 'var(--eds-space-16)' }}
            />
          ))}
        </>
      ) : (
        <div
          className={`eds-flex eds-flex--justify-content-center eds-flex--align-items-flex-center`}
        >
          <Table width="100%" className={styles.table_container}>
            <thead>
              <tr key={'passheader'} className="grid-header">
                {columns.map(column => {
                  if (
                    (column.headerName === ts.GRID_HEADER_COMPANY.stringKey &&
                      visitorPassFilter.passType === 10) ||
                    (column.headerName == 'PASS NUMBER' && !showPromocodeColumn())
                  ) {
                    return null;
                  } else {
                    return (
                      <TableHeading
                        className={styles.table_header}
                        data-testid={column.headerName}
                        key={column.headerName}
                      >
                        <span className={`eds-flex eds-flex--align-items-center`}>
                          <span className={`table_header_style`}>
                            {getTranslation(column.headerName)}
                          </span>
                          <IconButton
                            icon={
                              sorting.key === column.field
                                ? sorting.asc
                                  ? 'SortAscending'
                                  : 'SortDescending'
                                : 'Sort'
                            }
                            label="SortIcon"
                            variant="ghost"
                            size="small"
                            aria-label={`Sort by ${getTranslation(column.headerName)}, ${
                              sorting.key === column.field
                                ? sorting.asc
                                  ? 'currently sorted ascending'
                                  : 'currently sorted descending'
                                : 'not sorted'
                            }`}
                            onClick={() => applySorting(column.field)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                applySorting(column.field);
                              }
                            }}
                            id={`sort-button-${column.field}`}
                          ></IconButton>
                        </span>
                      </TableHeading>
                    );
                  }
                })}
                <TableHeading className={styles.table_header} key={'actions'}>
                  {i18nString(ts.GRID_HEADER_ACTIONS)}
                </TableHeading>
              </tr>
            </thead>
            <tbody>
              {visitorPasses?.passes &&
                visitorPasses?.passes.map(cell => {
                  return (
                    <tr key={'pass' + randomKey()} className={styles.table_row}>
                      <TableCell
                        className={styles.table_td + ' ' + styles.grid_cell_name}
                        key={cell.visitorId}
                        data-label={getTranslation(columns[0].headerName)}
                      >
                        {getName(cell)}
                      </TableCell>
                      {visitorPassFilter.passType != 10 && (
                        <TableCell
                          className={styles.table_td}
                          key={cell.visitorId + '-company'}
                          data-label={getTranslation(columns[1].headerName)}
                        >
                          {cell.companyName}
                        </TableCell>
                      )}
                      <TableCell
                        className={styles.table_td}
                        key={cell.visitorId + '-type'}
                        data-label={getTranslation(columns[2].headerName)}
                      >
                        {getTranslation(cell.passTypeToken)}
                      </TableCell>
                      <TableCell
                        className={styles.table_td}
                        key={cell.visitorId + '-policy'}
                        data-label={getTranslation(columns[3].headerName)}
                      >
                        {cell.passPolicy}
                      </TableCell>
                      <TableCell
                        className={styles.table_td}
                        key={cell.visitorId + '-date'}
                        data-label={getTranslation(columns[4].headerName)}
                      >
                        {formatDate(cell, userInfo?.passplayUser?.userCountry || '')}
                      </TableCell>
                      {showPromocodeColumn() && (
                        <TableCell
                          className={styles.table_td}
                          key={cell.visitorId + '-alternateId'}
                          data-label={getTranslation(columns[5].headerName)}
                        >
                          {cell.alternateId}
                        </TableCell>
                      )}
                      <TableCell
                        className={styles.table_td}
                        key={cell.visitorId + '-status'}
                        data-label={getTranslation(columns[6].headerName)}
                      >
                        {getTranslation(cell.statusToken)}
                      </TableCell>
                      <TableCell
                        key={cell.visitorId + '-actions'}
                        data-label={i18nString(ts.GRID_HEADER_ACTIONS)}
                      >
                        <IconButton
                          label="edit pass"
                          aria-label="edit pass"
                          size="small"
                          disabled={disableEdit(cell)}
                          className="cursor_pointer"
                          onClick={() => {
                            setEditPassProps({
                              ...editPassProps,
                              modalOpen: true,
                              passToEdit: cell,
                            });
                          }}
                          icon={'Edit'}
                          variant="ghost"
                        ></IconButton>
                        <IconButton
                          label="resend email"
                          aria-label="resend email"
                          className="cursor_pointer"
                          size="small"
                          onClick={() => {
                            setPassProps({ ...passProps, isResendVisible: true, pass: cell });
                          }}
                          disabled={disableResendEmail(cell)}
                          icon={'InboxFilled'}
                          variant="ghost"
                          data-testid="resend-email"
                        ></IconButton>
                        <IconButton
                          label="decline pass"
                          aria-label="decline pass"
                          className="cursor_pointer"
                          size="small"
                          onClick={() => {
                            setPassProps({ ...passProps, isVisible: true, pass: cell });
                          }}
                          disabled={disableDecline(cell)}
                          icon={'Close'}
                          variant="ghost"
                          data-testid="decline-pass"
                        ></IconButton>
                      </TableCell>
                    </tr>
                  );
                })}
              {visitorPasses?.passes?.length === 0 && (
                <tr>
                  <TableCell
                    className={styles.table_td}
                    colSpan={6}
                    data-label={i18nString(ts.GRID_HEADER_NAME)}
                  >
                    {i18nString(ts.GRID_EMPTY_TEXT_PASS)}
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
      <CancelPassModal {...passProps} setSnackbar={setSnackBar} />
      <ResendEmailModal {...passProps} setSnackbar={setSnackBar} />
      {!!editPassProps.passToEdit && <Pass {...editPassProps} setPassSnackbar={setSnackBar} />}
    </section>
  );
};
